import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';

import theme from './theme/theme';
import { AuthProvider } from './contexts/AuthContext';
import { FavoritesProvider } from './contexts/FavoritesContext';

import Header from './components/organisms/Header/Header';
import ProtectedRoute from './components/ProtectedRoute';
import MapsPlacesLayout from './layouts/MapsPlacesLayout';

const HomePage = lazy(() => import('./pages/HomePage'));
const ListPage = lazy(() => import('./pages/ListPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminHomePage = lazy(() => import('./pages/admin/AdminHomePage'));
const ReviewsAdminPage = lazy(() => import('./pages/admin/ReviewsAdminPage'));
const PropertiesManagementPage = lazy(() => import('./pages/admin/PropertiesManagementPage'));
const FeaturedPropertiesPage = lazy(() => import('./pages/admin/FeaturedPropertiesPage'));
const AdminPropertyDetailPage = lazy(() => import('./pages/admin/PropertyDetailPage'));
const UsersManagementPage = lazy(() => import('./pages/admin/UsersManagementPage'));
const AnalyticsDashboardPage = lazy(() => import('./pages/admin/AnalyticsDashboardPage'));
const EpisodesManagementPage = lazy(() => import('./pages/admin/EpisodesManagementPage'));
const PhotoCheckPage = lazy(() => import('./pages/admin/PhotoCheckPage'));
const AdminManualPage = lazy(() => import('./pages/admin/AdminManualPage'));

const RouteFallback = () => (
	<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, p: 4 }}>
		<CircularProgress aria-label="Loading page" />
	</Box>
);

const App = () => {
	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<AuthProvider>
				<FavoritesProvider>
					<Box
						sx={{
							height: '100vh',
							display: 'flex',
							flexDirection: 'column',
						}}
						className="App"
					>
						<Header />
						<Box
							sx={{
								flex: 1,
								overflow: 'auto',
							}}
						>
							<Suspense fallback={<RouteFallback />}>
								<Routes>
									<Route element={<MapsPlacesLayout />}>
										<Route path="/" element={<HomePage />} />
										<Route path="/list" element={<ListPage />} />
										<Route path="/property/:propertyId" element={<PropertyDetailPage />} />
									</Route>

									<Route path="/about" element={<AboutPage />} />

									<Route path="/login" element={<LoginPage />} />
									<Route path="/register" element={<RegisterPage />} />
									<Route path="/forgot-password" element={<ForgotPasswordPage />} />

									<Route
										path="/profile"
										element={
											<ProtectedRoute>
												<ProfilePage />
											</ProtectedRoute>
										}
									/>
									<Route
										path="/favorites"
										element={
											<ProtectedRoute>
												<FavoritesPage />
											</ProtectedRoute>
										}
									/>

									<Route
										path="/admin"
										element={
											<ProtectedRoute requiredRole="admin">
												<AdminDashboard />
											</ProtectedRoute>
										}
									>
										<Route index element={<AdminHomePage />} />
										<Route path="reviews" element={<ReviewsAdminPage />} />
										<Route path="properties" element={<PropertiesManagementPage />} />
										<Route path="featured-properties" element={<FeaturedPropertiesPage />} />
										<Route path="properties/:propertyId/detail" element={<AdminPropertyDetailPage />} />
										<Route path="users" element={<UsersManagementPage />} />
										<Route path="analytics" element={<AnalyticsDashboardPage />} />
										<Route path="episodes" element={<EpisodesManagementPage />} />
										<Route path="photo-check" element={<PhotoCheckPage />} />
										<Route path="manual" element={<AdminManualPage />} />
									</Route>

									<Route path="/unauthorized" element={<UnauthorizedPage />} />
									<Route path="*" element={<NotFoundPage />} />
								</Routes>
							</Suspense>
						</Box>
					</Box>
				</FavoritesProvider>
			</AuthProvider>
		</ThemeProvider>
	);
};

export default App;
